import React, { useCallback, useMemo } from 'react';

import { Stack } from '@/components/layout';
import { Skeleton, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useGuardianPlayersQuery } from '@/features/guardian/queries';
import { toMatchedPlayer, type MatchedPlayer } from '@/features/onboarding/utils/player-card';
import { createStyles, useScaling } from '@/hooks';
import type { GuardianStackScreenProps } from '@/navigations/types';
import { useSessionStore } from '@/stores/session-store';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import AddPlayerTile from './add-player-tile';
import MyKidCard from './card';
import {
  MOBILE_GRID_GAP,
  MOBILE_GRID_ROW_GAP,
  SECTION_PADDING_X,
  SKELETON_CARD_HEIGHT,
  TABLET_GRID_GAP,
  TABLET_GRID_ROW_GAP,
} from './constants';

type MyKidsSectionProps = {
  /** Opens the Add Player screen (PP-376). */
  onAddPlayer?: () => void;
};

function MyKidsSection({ onAddPlayer }: MyKidsSectionProps) {
  const styles = useStyles();
  const navigation = useNavigation<GuardianStackScreenProps<'GuardianDashboard'>['navigation']>();
  const { scale } = useScaling();
  const uid = useSessionStore(s => s.user?.uid);
  const { data: guardianPlayers, isLoading } = useGuardianPlayersQuery(uid);

  const gridGap = scale({ phone: MOBILE_GRID_GAP, tablet: TABLET_GRID_GAP });
  const gridRowGap = scale({ phone: MOBILE_GRID_ROW_GAP, tablet: TABLET_GRID_ROW_GAP });

  const kids = useMemo(() => {
    return (guardianPlayers ?? []).map(toMatchedPlayer);
  }, [guardianPlayers]);

  const kidRows = useMemo(() => {
    const rows: MatchedPlayer[][] = [];
    for (let index = 0; index < kids.length; index += 2) {
      rows.push(kids.slice(index, index + 2));
    }
    return rows;
  }, [kids]);

  const handleOpenPlayerProfile = useCallback(
    (playerId: string) => {
      const player = guardianPlayers?.find(item => item.id === playerId);

      if (!player) {
        return;
      }

      navigation.navigate('PlayerDetail', { player });
    },
    [guardianPlayers, navigation],
  );

  const renderKid = useCallback(
    (item: MatchedPlayer) => (
      <Stack key={item.id} flex={1} minWidth={0}>
        <MyKidCard
          name={item.name}
          level={item.level}
          progress={item.progress}
          avatarSource={item.avatar}
          onPress={() => handleOpenPlayerProfile(item.id)}
        />
      </Stack>
    ),
    [handleOpenPlayerProfile],
  );

  if (!isLoading && kids.length === 0) return null;

  // Odd player count → the "Add a player" tile fills the empty cell beside the last player.
  // Even count → a full-width "Add a player" button sits below the grid (PP-376).
  const isOddCount = kids.length % 2 === 1;

  return (
    <Stack width="100%" paddingTop={scale(16)} paddingX={scale(SECTION_PADDING_X)}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {STRINGS.guardian.home.myKids}
      </Text>
      {isLoading ? (
        <Stack width="100%" direction="row" gap={gridGap}>
          <Stack flex={1} minWidth={0}>
            <Skeleton width="100%" height={SKELETON_CARD_HEIGHT} borderRadius={scale(12)} />
          </Stack>
          <Stack flex={1} minWidth={0}>
            <Skeleton width="100%" height={SKELETON_CARD_HEIGHT} borderRadius={scale(12)} />
          </Stack>
        </Stack>
      ) : (
        <Stack width="100%" gap={gridRowGap}>
          {kidRows.map((row, rowIndex) => {
            const isLastRow = rowIndex === kidRows.length - 1;
            return (
              <Stack key={row[0].id} width="100%" direction="row" gap={gridGap}>
                {row.map(renderKid)}
                {isLastRow && isOddCount ? (
                  <Stack flex={1} minWidth={0}>
                    <AddPlayerTile variant="tile" onPress={onAddPlayer} />
                  </Stack>
                ) : null}
              </Stack>
            );
          })}
          {isOddCount ? null : <AddPlayerTile variant="wide" onPress={onAddPlayer} />}
        </Stack>
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  title: { color: colors['10'], paddingBottom: scale(8) },
}));

export default MyKidsSection;
